import { Download } from '@mui/icons-material';
import { Alert } from '@mui/material';
import { MutableRefObject, useEffect, useRef, useState } from 'react';
import { FsokPaper } from '../../../UIComponents/FsokPaper/FsokPaper';
import { formatSwedishDateTime } from '../../../common/lib/formatSwedishDateTime';
import { FormError } from '../../../types/FormError';
import * as varderingClient from '../../clients/VarderingClient';
import {
  createAdressLine,
  createAdressLines,
} from '../../lib/createAdressLine';
import { formatArea } from '../../lib/formatArea';
import { formatSwedishCurrency } from '../../lib/formatSwedishCurrency';
import {
  BostadsvarderingDto,
  Kategori,
} from '../../types/dtos/VarderingarModel';
import { Information } from './Information';
import { Referensforsaljningar } from './Referensforsaljningar';
import styles from './VarderingRapport.module.css';
import pdf from './pdf.svg';

interface VarderingRapportProps {
  bostadsvardering: BostadsvarderingDto;
  varderingElement: MutableRefObject<HTMLDivElement>;
}

export const VarderingRapport: React.FC<VarderingRapportProps> = ({
  bostadsvardering,
  varderingElement,
}) => {
  const [pdfLoadError, setPdfLoadError] = useState<FormError>();
  const [mapLoadError, setMapLoadError] = useState<FormError>();
  const mapRef = useRef<HTMLImageElement>();

  useEffect(() => {
    const downloadImage = async () => {
      setMapLoadError(undefined);
      const response = await varderingClient.getKartaForVarderingar(
        bostadsvardering.id
      );

      if (response.success) {
        const image = response.getBlobAsString();
        mapRef.current.src = image;
        mapRef.current.style.visibility = 'visible';
        return;
      }
      setMapLoadError({
        severity: 'error',
        text: `Oväntat fel när karta hämtades${`: ${response.error}` || '.'}`,
      });
    };

    if (bostadsvardering) {
      mapRef.current.style.visibility = 'hidden';
      mapRef.current.src = '';
      downloadImage();
    }
  }, [bostadsvardering]);

  const createPdfFilename = (vardering: BostadsvarderingDto) =>
    createAdressLine(vardering.adress)
      .toLocaleLowerCase()
      .replaceAll(/ /g, '_')
      .replaceAll(/[^0-9a-z_åäö-]/g, '');

  const downloadPdf = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    setPdfLoadError(undefined);
    const response = await varderingClient.getPdfForVardering(
      bostadsvardering.id
    );
    if (!response.success) {
      return setPdfLoadError({
        severity: 'error',
        text: `Oväntat fel när pdf hämtades${
          response.error ? `: ${response.error}` : '.'
        }`,
      });
    }
    response.downloadBlob(createPdfFilename(bostadsvardering));
  };

  const parametrar = bostadsvardering.parametrar;

  return (
    <div ref={varderingElement}>
      <FsokPaper className={styles.rapportContainer}>
        <a
          href={`${varderingClient.baseUrl()}/varderingar/${
            bostadsvardering.id
          }/pdf`}
          aria-hidden="true"
          onClick={downloadPdf}
          className={styles.pdfLink}
        >
          <img src={pdf} alt="PDF" className={styles.pdfButton} />
          <Download className={styles.downloadIcon} />
        </a>
        {pdfLoadError && (
          <Alert sx={{ mt: 2 }} severity={pdfLoadError.severity}>
            {pdfLoadError.text}
          </Alert>
        )}
        <div>
          <div className={styles.rapportBlock}>
            <div className={styles.rapportBlockRubrik}>Bostadsuppgifter</div>
            <div className={styles.infoRow}>
              <Information
                label="Adress"
                information={createAdressLines(bostadsvardering.adress)}
                width={2}
              />
            </div>
            <div className={styles.infoRow}>
              <Information
                label="Bostadskategori"
                information={mapKategori(bostadsvardering.parametrar.kategori)}
              />
            </div>
            <div className={styles.infoRow}>
              <Information
                label="Boarea"
                information={formatArea(parametrar.boarea)}
              />
              <Information
                label="Biarea"
                information={formatArea(parametrar.biarea)}
              />
              <Information
                label="Tomtarea"
                information={formatArea(parametrar.tomtarea)}
              />
              <Information label="Byggår" information={parametrar.byggar} />
            </div>
            <div className={styles.infoRow}>
              <Information
                label="Antal rum"
                information={parametrar.antalRum}
              />
              <Information label="Avgift" information={parametrar.avgift} />
              <Information label="Våning" information={parametrar.vaning} />
              <Information
                label="Antal våningar"
                information={parametrar.antalVaningar}
              />
            </div>
            <div className={styles.infoRow}>
              <Information
                label="Standardpoäng"
                information={parametrar.standardpoang}
              />
            </div>
          </div>
          <div className={styles.rapportBlock}>
            <div className={styles.rapportBlockRubrik}>Värdering</div>
            <div className={styles.infoRow}>
              <Information
                label="Värdering"
                information={formatSwedishCurrency(
                  bostadsvardering.vardering.vardering,
                  'kr'
                )}
              />
              <Information
                label="Kvalitet (1-5)"
                information={mapKvalitet(bostadsvardering.vardering.kvalitet)}
              />
              <Information
                label="Tidpunkt"
                information={formatSwedishDateTime(
                  bostadsvardering.vardering.tidpunkt
                )}
              />
            </div>
          </div>
          <div className={styles.rapportBlock}>
            <div className={styles.rapportBlockRubrik}>Läge, karta</div>
            <div className={styles.infoRow}>
              <Information
                label="N (SWEREF99 TM)"
                information={bostadsvardering.parametrar.sweref99y.toFixed(1)}
              />
              <Information
                label="E (SWEREF99 TM)"
                information={bostadsvardering.parametrar.sweref99x.toFixed(1)}
              />
            </div>
            <div className={styles.infoRow}>
              <img
                className={styles.karta}
                style={{ visibility: 'hidden' }}
                alt="Karta"
                ref={mapRef}
              />
              {mapLoadError && (
                <div>
                  <Alert severity={mapLoadError.severity}>
                    {mapLoadError.text}
                  </Alert>
                </div>
              )}
            </div>
          </div>
          <div className={styles.rapportBlock}>
            <div className={styles.rapportBlockRubrik}>
              Referensförsäljningar
            </div>
            <div className={styles.infoRow}>
              {bostadsvardering.referensforsaljningar?.length ? (
                <Referensforsaljningar
                  referensforsaljningar={bostadsvardering.referensforsaljningar}
                />
              ) : (
                <Alert sx={{ mt: 2 }} severity="info">
                  Inga referensförsäljningar hittades
                </Alert>
              )}
            </div>
          </div>
        </div>
      </FsokPaper>
    </div>
  );
};

const mapKvalitet = (kvalitet: number): string =>
  ({
    1: '1 - Bör ej användas',
    2: '2 - Låg kvalitet',
    3: '3 - Godkänd kvalitet',
    4: '4 - God kvalitet',
    5: '5 - Hög kvalitet',
  }[kvalitet]);

const mapKategori = (kategori: Kategori): string =>
  ({ [Kategori.BOSTADSRATT]: 'Bostadsrätt', [Kategori.SMAHUS]: 'Småhus' }[
    kategori
  ]);
